import { hash128ToUnityHex, spookyHash128 } from "@models/utils/spookyHash";

/**
 * `sharedLinkId.ts` — the deterministic id of a shared-link row.
 *
 * Port of `SharedLinkData.GenerateID` (`Models/Data/SharedLinkData.cs:144`):
 *
 * ```csharp
 * var hash = new Hash128();
 * if (!string.IsNullOrEmpty(projectId))    hash.Append(projectId);
 * if (!string.IsNullOrEmpty(annotationId)) hash.Append(annotationId);
 * if (!string.IsNullOrEmpty(email))        hash.Append(email);
 * return hash.ToString();
 * ```
 *
 * WHY THIS HAS TO MATCH BYTE FOR BYTE, rather than being "some stable id":
 * the desktop client REGENERATES this id to look a row up —
 * `CustomerModel.FindAndApplySharedLink` computes `GenerateID(projectId,
 * currentUser.Email)` and does a point `GetSharedLink` on it when a user
 * moves from an account-wide grant to an individual one — and a desktop
 * re-invite `PutItem`s the same id, so it overwrites rather than duplicates.
 * A web row under any other id is invisible to the first and duplicated by
 * the second. Until 2026-09 the web minted random UUID hex here, which is
 * exactly that failure.
 *
 * Unity `Hash128` semantics reproduced here:
 *  - `Hash128` IS SpookyHash V2 (128-bit). `new Hash128()` is all zeros.
 *  - `Append(x)` hashes `x` SEEDED WITH THE CURRENT VALUE and stores the
 *    result — i.e. `SpookyHash::Hash128(bytes, len, &u64[0], &u64[1])` with
 *    the in/out seed, not a streaming update over the concatenation. The two
 *    differ, and only this one is Unity's.
 *  - A `string` is appended as its UTF-8 bytes.
 *  - `ToString()` prints the 16 bytes in memory order (`hash128ToUnityHex`).
 *
 * ⚠ The last three bullets are verified against desktop-created rows in the
 * dev account by the AI-1341 M1 oracle harness (see the Work Plan bullet). If
 * you change any of them, re-run it — a plausible-looking 32-hex id that the
 * desktop cannot find is worse than the random one it replaced.
 */
export function generateSharedLinkId(
  projectId: string | null | undefined,
  email: string | null | undefined,
  annotationId?: string | null,
): string {
  let h1 = 0n;
  let h2 = 0n;
  const append = (value: string | null | undefined): void => {
    if (!value) return;
    [h1, h2] = spookyHash128(new TextEncoder().encode(value), h1, h2);
  };
  append(projectId);
  append(annotationId);
  append(email);
  return hash128ToUnityHex(h1, h2);
}
